import React, { useMemo, useEffect, useContext } from 'react';
import { BoxProps } from '@tapresearch/ui/components';
import { FilterBadges } from '../../../common/Filter';

import { filterContext } from '../../../common/FilterProvider';

import {
  QualificationWithQualificationAnswers,
  useGetQualificationsByIds,
} from '../../../../api/QualificationAPI';
import {
  QualificationAnswer,
  Qualification,
} from '../../../../api/insightsTypes';

type CustomQualificationAnswer =
  Qualification['attributes']['custom_qualification_answers'][number];

export interface LongitudinalFilterBadgesProps extends BoxProps {
  isDisabled?: boolean;
  onTagClicked?: () => void;
}

export function LongitudinalFilterBadges({
  isDisabled,
  onTagClicked = () => {
    //
  },
  ...rest
}: LongitudinalFilterBadgesProps) {
  const context = useContext(filterContext);
  const { filterValues, onFilterItemLabelChange } = context;

  const qualificationIds = Object.keys(filterValues).map((qId) =>
    parseInt(qId)
  );

  const queries = useGetQualificationsByIds(qualificationIds, true, true);
  const isLoading = queries.some((query) => query.isLoading);

  const { filterNames, qualificationsByQuestionId } = useMemo(() => {
    if (!isLoading) {
      const qualificationsByQuestionId: Record<
        string,
        QualificationWithQualificationAnswers
      > = {};
      const filterNames = queries.reduce<Record<string, string>>(
        (acc, query) => {
          const qualification = query.data.data.data;
          acc[qualification.attributes.question_id] =
            qualification.attributes.name;
          qualificationsByQuestionId[qualification.attributes.question_id] =
            qualification as QualificationWithQualificationAnswers;
          return acc;
        },
        {}
      );

      return { filterNames, qualificationsByQuestionId };
    }
    return { filterNames: {}, qualificationsById: {} };
  }, [isLoading, queries]);

  useEffect(() => {
    if (!isLoading) {
      Object.entries(filterValues).forEach(([qId, values]) => {
        const qualification = qualificationsByQuestionId[qId];

        const answers = (qualification.attributes
          .custom_qualification_answers ||
          qualification.attributes.qualification_answers) as (
          | CustomQualificationAnswer
          | QualificationAnswer
        )[];

        if (answers) {
          const answersByPreCode = answers.reduce<
            Record<string, QualificationAnswer | CustomQualificationAnswer>
          >((acc, answer) => {
            acc[answer.attributes.pre_code] = answer;
            return acc;
          }, {});

          const labels = (values as string[])
            .map((filterValue) => {
              return (answersByPreCode[filterValue] as QualificationAnswer)
                .attributes?.option_text;
            })
            .join(', ');

          onFilterItemLabelChange(qId, labels);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filterValues), isLoading]);

  return (
    <>
      {!isLoading && (
        <FilterBadges
          {...rest}
          filterNames={filterNames}
          onTagClicked={onTagClicked}
          isDisabled={isDisabled}
        />
      )}
    </>
  );
}
